import React, { useContext } from 'react';
import { ThemeContext } from '../../context/Themes';
import { AreaChart, Area, XAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const fmtCurrency = (n) => `${Number(n ?? 0).toLocaleString('vi-VN')}đ`;
const fmtPct = (n) => (n == null ? '—' : `${n >= 0 ? '+' : ''}${Number(n).toFixed(1)}%`);

/**
 * mode='money' (mặc định cũ): data [{ label, purchase, theoretical }] — tiền mua vs tiền lý thuyết,
 * vẽ chồng (không stack) để đọc thẳng khoảng cách giữa 2 đường.
 * mode='qty' (#142018): data [{ label, medianPct, overThreshold }] — % hao hụt theo SỐ LƯỢNG. Vẽ
 * TRUNG VỊ % của từng NVL chứ không phải % gộp, vì số lượng khác đơn vị (kg/lít/cái) không cộng
 * được; số NVL vượt ngưỡng hiện trong tooltip.
 * #153458: `medianPct` nay là CÔNG THỨC MỚI (tồn đầu kỳ + nhập − định mức − tồn cuối kỳ). Nếu điểm
 * dữ liệu có thêm `legacyMedianPct` thì vẽ kèm đường đứt nét của CÔNG THỨC CŨ (mua − lý thuyết) để
 * đọc thẳng chênh lệch giữa hai cách tính trên cùng một trục.
 */
export default function ShrinkageChartComponent({ data = [], mode = 'money' }) {
  const { theme } = useContext(ThemeContext);
  const dark = { border: '#39496b', frame: '#2f3f61', title: '#f0f0f0', text: '#d1d1d1', bg: '#1b2b4d' };
  const light = { border: '#d1d1d1', frame: '#f0f0f0', title: '#403e57', text: '#5e5d72', bg: '#ffffff' };
  const palette = theme !== 'light_mode' ? dark : light;

  if (data.length < 2) {
    return (
      <p className="text-muted text-center py-5 mb-0">
        Cần ít nhất 2 mốc thời gian để vẽ biểu đồ — chọn khoảng ngày rộng hơn hoặc đổi độ chi tiết.
      </p>
    );
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div
        style={{
          background: palette.bg,
          borderRadius: '8px',
          padding: '8px 15px 9px',
          border: `1px solid ${palette.frame}`,
          boxShadow: '0px 12px 25px 0px rgb(0 0 0 / 12%)',
        }}
      >
        <p style={{ color: palette.title, fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', margin: 0 }}>
          {label}
        </p>
        {payload.map((p) => (
          <p key={p.dataKey} style={{ color: p.stroke, fontSize: '13px', fontWeight: 500, margin: '2px 0 0' }}>
            {p.name}: {mode === 'qty' ? fmtPct(p.value) : fmtCurrency(p.value)}
          </p>
        ))}
        {mode === 'qty' && (
          <p style={{ color: palette.text, fontSize: '12px', margin: '2px 0 0' }}>
            NVL vượt ngưỡng: {payload[0]?.payload?.overThreshold ?? 0}
          </p>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" aspect={2.4} maxHeight={320}>
      <AreaChart data={data}>
        <defs>
          <linearGradient id="shrinkagePurchase" x1="0" y1="0" x2="0" y2="1">
            <stop offset="35%" stopColor="#2351af" stopOpacity={0.35} />
            <stop offset="85%" stopColor="#2351af" stopOpacity={0.03} />
          </linearGradient>
          <linearGradient id="shrinkageTheoretical" x1="0" y1="0" x2="0" y2="1">
            <stop offset="35%" stopColor="#e8590c" stopOpacity={0.35} />
            <stop offset="85%" stopColor="#e8590c" stopOpacity={0.03} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={palette.border} strokeDasharray="1 3" />
        <XAxis
          dataKey="label"
          tickSize={0}
          tickMargin={10}
          axisLine={false}
          stroke={palette.text}
          fontSize="11px"
          fontWeight="500"
          interval="preserveStartEnd"
        />
        <Tooltip cursor={false} content={<CustomTooltip />} />
        <Legend wrapperStyle={{ fontSize: '12px' }} />
        {mode === 'qty' ? (
          <>
            <Area
              type="monotone"
              dataKey="medianPct"
              name="Công thức MỚI — tồn đầu/cuối kỳ (trung vị theo NVL)"
              stroke="#e8590c"
              strokeWidth={2}
              fill="url(#shrinkageTheoretical)"
              connectNulls
            />
            {data.some((d) => d.legacyMedianPct != null) && (
              <Area
                type="monotone"
                dataKey="legacyMedianPct"
                name="Công thức CŨ — mua trừ lý thuyết"
                stroke="#868e96"
                strokeWidth={2}
                strokeDasharray="4 3"
                fill="none"
                connectNulls
              />
            )}
          </>
        ) : (
          <>
            <Area type="monotone" dataKey="purchase" name="Tiền mua NVL" stroke="#2351af" strokeWidth={2} fill="url(#shrinkagePurchase)" />
            <Area type="monotone" dataKey="theoretical" name="Tiền NVL lý thuyết" stroke="#e8590c" strokeWidth={2} fill="url(#shrinkageTheoretical)" />
          </>
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}
